import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import LegalContent, { LegalContentType } from './LegalContent';
import ListItemButton from '@mui/material/ListItemButton';
import List from '@mui/material/List';
import CustomLoader from '@common/components/Feedback/CustomLoader';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import ListItemText from '@mui/material/ListItemText';
import { useSearchParams } from 'react-router-dom';
import { parseStringFromEnum, TAB_QUERY_STRING } from 'utils/url-tabs-helper';
import { FilterKeys } from '@common/enums/query-params/filters';
import { PageTabs } from './LegalPage';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

const ChangeHistory: React.FC<{ date?: string; type?: LegalContentType; language: string }> = ({
  date,
  type,
  language,
}) => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const [manifest, setManifest] = useState<Record<
    string,
    { date: string; files: Record<string, string> }[]
  > | null>(null);
  const [selected, setSelected] = useState<{ type: LegalContentType; date: string } | null>(null);
  const [error, setError] = useState(false);

  const getAllFiles = useCallback(
    (
      manifest: Record<LegalContentType, { date: string; files: { [language: string]: string } }[]>,
    ) =>
      Object.entries(manifest)
        .flatMap(([type, entries]) =>
          entries
            .filter((e) => e.files[language])
            .map((e) => ({ type: type as LegalContentType, ...e })),
        )
        .sort((a, b) => b.date.localeCompare(a.date)),
    [language],
  );

  useEffect(() => {
    const loadFiles = async () => {
      try {
        const response = await fetch('/legal/manifest.json');
        if (!response.ok) throw new Error();

        const manifest = await response.json();
        setManifest(manifest);

        const allFiles = getAllFiles(manifest);
        const latestFile = allFiles[0];
        const fileToShow =
          date && type && allFiles.find((f) => f.date === date && f.type === type)
            ? allFiles.find((f) => f.date === date && f.type === type)
            : latestFile;

        if (fileToShow) {
          setSelected(fileToShow);
          setFilterParams((prev) => {
            prev.set(
              TAB_QUERY_STRING,
              parseStringFromEnum(
                PageTabs,
                fileToShow.type === LegalContentType.TermsOfService
                  ? PageTabs.TermsOfService
                  : PageTabs.PrivacyPolicy,
              ),
            );
            prev.set(FilterKeys.StartDate, fileToShow.date);
            return prev;
          });
        }
      } catch {
        setError(true);
      }
    };

    loadFiles();
  }, [date, language, setFilterParams, type, getAllFiles]);

  const selectHandler = (file: {
    type: LegalContentType;
    date: string;
    files: Record<string, string>;
  }) => {
    setSelected({ type: file.type, date: file.date });
    setFilterParams((prev) => {
      prev.set(
        TAB_QUERY_STRING,
        parseStringFromEnum(
          PageTabs,
          file.type === LegalContentType.TermsOfService
            ? PageTabs.TermsOfService
            : PageTabs.PrivacyPolicy,
        ),
      );
      prev.set(FilterKeys.StartDate, file.date);
      return prev;
    });
  };

  if (error) return <CustomAlert severity="error" header={t('error-loading-content')} />;
  if (!manifest) return <CustomLoader />;

  const allFiles = getAllFiles(manifest);

  return (
    <Stack direction="row" spacing={2} divider={<Divider flexItem orientation="vertical" />}>
      <List>
        {allFiles.map((f) => (
          <ListItemButton
            key={f.files[language]}
            selected={selected?.date === f.date && selected?.type === f.type}
            onClick={() => selectHandler(f)}
          >
            <ListItemText
              primary={t(`title-${f.type}`)}
              secondary={new Date(f.date).toLocaleDateString()}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItemButton>
        ))}
      </List>
      {selected && (
        <Box sx={{ width: '100%' }}>
          <LegalContent type={selected.type} date={selected.date} language={language} />
        </Box>
      )}
    </Stack>
  );
};

export default ChangeHistory;
